import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { observer, inject } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { HelpIcon, TooltipText } from '../../../components'
import { numberToDollars } from '../../../utils'
import { MOBILE_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import {
  PROJECTIONS_VALUE_INSET,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionHeadingHelp,
  SectionHeadingTail,
  SectionFootnote,
  ProjectionsChart,
  ProjectionsTrack,
  ProjectionsReserve,
  ProjectionsBar,
  ProjectionsValue,
  ProjectionsDollarSign,
  ProjectionsOutsideValue,
  ProjectionsYear,
  ProjectionsYearHelp,
  ProjectionsYearHelpSpacer,
  ProjectionsWhy,
  ProjectionsFootnote,
  ProjectionsLegend,
  ProjectionsLegendItem,
  ProjectionsSwatch,
} from './DashboardStyle'

const DESKTOP_FOOTNOTE = "* All projected values are shown in today's dollars."
const WHY_LABEL = 'Why is my projected balance lower?'

// Open Sans advance widths, as a fraction of the font size, for the characters a
// dollar amount can contain. Used to work out whether a value still fits inside
// its bar without having to measure the rendered text.
const VALUE_FONT_SIZE = 18
const COMMA_WIDTH = 0.26
const ASTERISK_WIDTH = 0.4
const DIGIT_WIDTH = 0.556
const BOLD_FACTOR = 1.05

const charWidth = char => {
  if (char === ',') {
    return COMMA_WIDTH
  }
  return char === '*' ? ASTERISK_WIDTH : DIGIT_WIDTH
}

const estimateValueWidth = (text, bold) =>
  [...text].reduce((total, char) => total + charWidth(char), 0) *
  VALUE_FONT_SIZE *
  (bold ? BOLD_FACTOR : 1)

// Engine projections can come back null, and a negative projection is reported as
// nothing left rather than a debt.
const projectedValue = engineCase => Math.max(Math.round(engineCase?.estimatedFutureValue ?? 0), 0)

// Snapshot both projections so the displayed pair always updates together.
const projectionValues = store =>
  store.baseCase && store.recommendedCase
    ? {
        current: projectedValue(store.baseCase),
        recommended: projectedValue(store.recommendedCase),
      }
    : null

// Width of the widest bar, in pixels. Both rows share it, so measuring the first
// track is enough. Stays null where ResizeObserver is unavailable, which leaves
// the values inside their bars — a measured 0 has to stay distinct from "not
// measured", or a track squeezed to nothing reads as room to spare and the
// layout oscillates. Uses a callback ref because the track only appears once the
// projections have loaded, after any mount effect would have run.
function useTrackWidth() {
  const [width, setWidth] = useState(null)
  const observerRef = useRef(null)

  const ref = useCallback(node => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!node || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return [ref, width]
}

const DashboardProjections = ({ store }) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const [trackRef, trackWidth] = useTrackWidth()
  const [shownValues, setShownValues] = useState(null)

  // Refetch cases when accounts change without remounting the page.
  const { accountsChangeKey } = store

  useEffect(() => {
    let cancelled = false

    // Capture the last complete pair in case either new projection fails to load.
    const priorValues = projectionValues(store)

    const loadCases = async () => {
      try {
        await store.getCases()
        if (!cancelled) {
          setShownValues(projectionValues(store))
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setShownValues(current => current ?? priorValues)
        }
      }
    }

    loadCases()
    return () => {
      cancelled = true
    }
  }, [store, accountsChangeKey])

  if (!shownValues) {
    return null
  }

  const year = store.person?.retirementYear
  const { current: currentValue, recommended: recommendedValue } = shownValues
  const largestValue = Math.max(currentValue, recommendedValue)

  const rows = [
    { key: 'current', label: 'current plan', value: currentValue, accent: false },
    { key: 'recommended', label: 'recommended plan', value: recommendedValue, accent: true },
  ].map(row => {
    // The asterisk points at the desktop footnote; mobile carries the same note in
    // its section heading instead.
    const amountText = `${numberToDollars(row.value, true)}${!isMobile && row.value > 0 ? '*' : ''}`

    return {
      ...row,
      amountText,
      // The dollar sign is coloured separately, but width still needs the whole thing.
      valueText: `$${amountText}`,
      percent: largestValue > 0 ? (row.value / largestValue) * 100 : 0,
    }
  })

  // A $0 case has no bar to sit in, and once one value has to move out both do.
  const fitsInsideBar = row =>
    row.value > 0 &&
    (trackWidth === null ||
      (trackWidth * row.percent) / 100 - PROJECTIONS_VALUE_INSET * 2 >=
        estimateValueWidth(row.valueText, row.accent))
  const valuesOutside = !rows.every(fitsInsideBar)

  // Hang the help off the row that lost its bar; off the current row when both did.
  const zeroRowKey =
    currentValue === 0 ? 'current' : recommendedValue === 0 ? 'recommended' : undefined

  const yearLabel = row => `${year} | ${row.label}`

  const valueContent = row => (
    <>
      <ProjectionsDollarSign accent={row.accent}>$</ProjectionsDollarSign>
      {row.amountText}
    </>
  )

  const renderRow = (row, index) => (
    <Fragment key={row.key}>
      <ProjectionsTrack
        data-testid={`projection-${row.key}`}
        ref={index === 0 ? trackRef : undefined}>
        {row.value > 0 && (
          <ProjectionsBar
            accent={row.accent}
            data-testid={`projection-bar-${row.key}`}
            style={{ width: `${row.percent}%` }}>
            {!valuesOutside && (
              <ProjectionsValue accent={row.accent}>{valueContent(row)}</ProjectionsValue>
            )}
          </ProjectionsBar>
        )}
        {valuesOutside && (
          <ProjectionsOutsideValue accent={row.accent}>{valueContent(row)}</ProjectionsOutsideValue>
        )}
        {!isMobile && (
          <ProjectionsYear accent={row.accent}>
            {yearLabel(row)}
            {row.accent && (
              <ProjectionsYearHelp>
                <HelpIcon
                  tooltip={TooltipText.whatYouCanExpect({ recommendedPlan: true })}
                  size='large'
                />
              </ProjectionsYearHelp>
            )}
          </ProjectionsYear>
        )}
        {!isMobile && zeroRowKey === row.key && (
          <ProjectionsWhy>
            <HelpIcon
              reverse
              tooltip={TooltipText.balanceHidden()}
              helpLabel={WHY_LABEL}
              size='large'
            />
          </ProjectionsWhy>
        )}
      </ProjectionsTrack>

      {/* Only a row with a bar pushes its labels past the track, so only that row
          needs room reserved beside it. A $0 row starts at the track's left edge
          and has the whole width to lay out in. */}
      <ProjectionsReserve aria-hidden='true'>
        {row.value > 0 && (
          <>
            {valuesOutside && (
              <ProjectionsOutsideValue accent={row.accent}>
                {valueContent(row)}
              </ProjectionsOutsideValue>
            )}
            {!isMobile && (
              <ProjectionsYear accent={row.accent}>
                {yearLabel(row)}
                {row.accent && <ProjectionsYearHelpSpacer />}
              </ProjectionsYear>
            )}
          </>
        )}
      </ProjectionsReserve>
    </Fragment>
  )

  // Mobile opens the rows up when a case has lost its bar; see ProjectionsChart.
  const chart = (
    <ProjectionsChart zeroCase={Boolean(zeroRowKey)}>{rows.map(renderRow)}</ProjectionsChart>
  )

  if (!isMobile) {
    return (
      <>
        {chart}
        {largestValue > 0 && <ProjectionsFootnote>{DESKTOP_FOOTNOTE}</ProjectionsFootnote>}
      </>
    )
  }

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          {`${year} projected `}
          <SectionHeadingTail>
            balance
            <SectionHeadingHelp>
              <HelpIcon
                tooltip={TooltipText.whatYouCanExpect({ recommendedPlan: true })}
                size='large'
              />
            </SectionHeadingHelp>
          </SectionHeadingTail>
        </SectionHeading>
        <SectionFootnote>{`* ${year} projected values in today’s dollars`}</SectionFootnote>
      </SectionHeader>
      <ProjectionsLegend>
        <ProjectionsLegendItem>
          <ProjectionsSwatch />
          Current
        </ProjectionsLegendItem>
        <ProjectionsLegendItem accent>
          <ProjectionsSwatch accent />
          Recommended
        </ProjectionsLegendItem>
      </ProjectionsLegend>
      {chart}
      {zeroRowKey && (
        <ProjectionsWhy>
          <HelpIcon
            reverse
            tooltip={TooltipText.balanceHidden()}
            helpLabel={WHY_LABEL}
            size='large'
          />
        </ProjectionsWhy>
      )}
    </SectionCard>
  )
}

export default inject('store')(observer(DashboardProjections))
